import { useState, useEffect, useCallback } from 'react';
import { getSupabaseClient } from '@/lib/supabase';

export interface SiteNotice {
  id: string;
  slot: number;
  text: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const NOTICE_SLOTS = [1, 2] as const;

const FALLBACK_NOTICES = [
  'Global sourcing from 500+ verified suppliers across 40 countries',
  'ISO 9001 & GMP certified manufacturing partners',
];

export function useNotices() {
  const [notices, setNotices] = useState<SiteNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const supabase = getSupabaseClient();
    if (!supabase) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('site_notices')
        .select('*')
        .order('slot', { ascending: true })
        .order('created_at', { ascending: false });

      if (dbError) throw dbError;
      setNotices((data as SiteNotice[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveNotice = useCallback(async (slot: number, text: string) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const trimmed = text.trim();
    if (!trimmed) throw new Error('Notice text is empty');

    setSaving(true);
    try {
      // Deactivate the current active notice for this slot (keep it as history)
      await supabase
        .from('site_notices')
        .update({ is_active: false })
        .eq('slot', slot)
        .eq('is_active', true);

      // Insert the new version as the active notice
      const { error: insertError } = await supabase
        .from('site_notices')
        .insert({ slot, text: trimmed, is_active: true });

      if (insertError) throw insertError;

      await load();
    } finally {
      setSaving(false);
    }
  }, [load]);

  const toggleActive = useCallback(async (id: string, isActive: boolean) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    setSaving(true);
    try {
      const { error: dbError } = await supabase
        .from('site_notices')
        .update({ is_active: isActive })
        .eq('id', id);

      if (dbError) throw dbError;

      await load();
    } finally {
      setSaving(false);
    }
  }, [load]);

  // Restore a history notice: deactivate any current active notice for its slot first
  const restoreNotice = useCallback(async (id: string, slot: number) => {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    setSaving(true);
    try {
      await supabase
        .from('site_notices')
        .update({ is_active: false })
        .eq('slot', slot)
        .eq('is_active', true);

      const { error: dbError } = await supabase
        .from('site_notices')
        .update({ is_active: true })
        .eq('id', id);

      if (dbError) throw dbError;

      await load();
    } finally {
      setSaving(false);
    }
  }, [load]);

  // Active notices ordered by slot (for the marquee)
  const activeNotices = notices
    .filter((n) => n.is_active)
    .sort((a, b) => a.slot - b.slot)
    .map((n) => n.text);

  const activeTexts = activeNotices.length > 0 ? activeNotices : FALLBACK_NOTICES;

  // Latest active notice per slot (for the editor)
  const activeBySlot = NOTICE_SLOTS.reduce<Record<number, SiteNotice | null>>((acc, slot) => {
    acc[slot] =
      notices.find((n) => n.slot === slot && n.is_active) || null;
    return acc;
  }, {});

  // History = inactive notices, newest first
  const history = notices
    .filter((n) => !n.is_active)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  return {
    notices,
    activeTexts,
    activeBySlot,
    history,
    loading,
    error,
    saving,
    saveNotice,
    toggleActive,
    restoreNotice,
    reload: load,
  };
}